
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  FormLabel,
  FormGroup,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const SchoolScreeningForm = () => {
  // State for student details
  const [studentData, setStudentData] = useState({
    name: '',
    age: '',
    gender: '',
    date: '',
    schoolName: '',
    classGrade: '',
    schoolType: '',
    medium: '',
    board: '',
    socioEconomicStatus: '',
    parentsEducation: ''
  });
  const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [downloadFormat, setDownloadFormat] = useState('excel');
  // State for ear examinations
  const [earExamination, setEarExamination] = useState({
    rightOtoscopic: '',
    leftOtoscopic: '',
    rightDischarge: 'No',
    leftDischarge: 'No',
    rightPain: 'No',
    leftPain: 'No',
    rightDeformity: 'No',
    leftDeformity: 'No',
    deformitySpecify: ''
  });

  // State for screening tests
  const [screeningTests, setScreeningTests] = useState({
    audiometryRight500: '',
    audiometryLeft500: '',
    audiometryRight1k: '',
    audiometryLeft1k: '',
    audiometryRight2k: '',
    audiometryLeft2k: '',
    audiometryRight4k: '',
    audiometryLeft4k: '',
    audiometryResultRight: '',
    audiometryResultLeft: '',
    tympanometryRight: '',
    tympanometryLeft: '',
    oaeRight: '',
    oaeLeft: ''
  });

  // State for questionnaire
  const [questionnaire, setQuestionnaire] = useState({
    selectedAgeGroup: '4-6',
    answers: {}
  });

  // State for overall result
  const [overallResult, setOverallResult] = useState(null);

  // Question sets by age group
  const questionsByAgeGroup = {
    '4-6': [
      "a. Does the student use unusually loud/soft voice when speaking?",
      "b. Does the student not get startled or surprised by loud noises or has problems in hearing environmental sounds? (i.e., school bell, people calling and/or talking to the student in social situations)",
      "c. Does the child complain of frequent ear pain/ear discharge?",
      "d. Does the child speak in 2 to 3 word sentences?"
    ],
    '6-8': [
      "a. Does the student turn head to position ear in the direction of the speaker",
      "b. Does the student not respond when addressed or called out",
      "c. Does the student use unusually loud/soft voice when speaking?",
      "d. Does the student face difficulty in following verbal directions or instructions in quiet or noisy situations?",
      "e. Does the student purposefully watch faces while listening during a conversation?",
      "f. Does the student not get startled or surprised by loud noises or has problems in hearing environmental sounds (i.e., school bell, people calling and/or talking to the student in social situations)",
      "g. Does the child complain of frequent ear pain/ear discharge?"
    ],
    '>8': [
      "a. Does the student often mispronounce words",
      "b. Does the student face difficulty in following verbal directions or instructions in quiet or noisy situations?",
      "c. Does the student purposefully watch faces while listening during a conversation?",
      "d. Does the student not get startled or surprised by loud noises or has problems in hearing environmental sounds (i.e., school bell, people calling and/or talking to the student in social situation)",
      "e. Does the child complain of frequent ear pain/ear discharge"
    ]
  };

  // Handle input changes
  const handleStudentChange = (e) => {
    const { name, value } = e.target;
    setStudentData({ ...studentData, [name]: value });
  };

  const handleEarChange = (e) => {
    const { name, value } = e.target;
    setEarExamination({ ...earExamination, [name]: value });
  };

  const handleScreeningChange = (e) => {
    const { name, value } = e.target;
    setScreeningTests({ ...screeningTests, [name]: value });
  };

  const handleQuestionChange = (index) => (e) => {
    const newAnswers = { ...questionnaire.answers, [index]: e.target.value };
    setQuestionnaire({ ...questionnaire, answers: newAnswers });
  };

  const handleAgeGroupChange = (e) => {
    setQuestionnaire({ 
      ...questionnaire, 
      selectedAgeGroup: e.target.value,
      answers: {} // Reset answers when age group changes
    });
  };

  // Calculate overall result
  useEffect(() => {
    // Check if any questionnaire answer is "Yes"
    const hasYesAnswer = Object.values(questionnaire.answers).some(answer => answer === 'Yes');
    
    // Check if all questions are answered with "No"
    const allQuestionsAnswered = Object.keys(questionnaire.answers).length === 
                                questionsByAgeGroup[questionnaire.selectedAgeGroup].length;
    const allNoAnswers = allQuestionsAnswered && 
                         Object.values(questionnaire.answers).every(answer => answer === 'No');
    
    if (hasYesAnswer) {
      setOverallResult('refer');
    } else if (allNoAnswers) {
      setOverallResult('pass');
    } else {
      setOverallResult(null);
    }
  }, [questionnaire]);

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  try {
    const formData = {
      studentData,
      earExamination,
      screeningTests,
      questionnaire,
      overallResult
    };

    console.log('Submitting data:', formData);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/school-screening`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      const result = await response.json();
      alert('School screening data saved successfully!');
      
      // Reset form after successful submission
      setStudentData({
        name: '',
        age: '',
        gender: '',
        date: '',
        schoolName: '',
        classGrade: '',
        schoolType: '',
        medium: '',
        board: '',
        socioEconomicStatus: '',
        parentsEducation: ''
      });
      
      setEarExamination({
        rightOtoscopic: '',
        leftOtoscopic: '',
        rightDischarge: 'No',
        leftDischarge: 'No',
        rightPain: 'No',
        leftPain: 'No',
        rightDeformity: 'No',
        leftDeformity: 'No',
        deformitySpecify: ''
      });
      
      setScreeningTests({
        audiometryRight500: '',
        audiometryLeft500: '',
        audiometryRight1k: '',
        audiometryLeft1k: '',
        audiometryRight2k: '',
        audiometryLeft2k: '',
        audiometryRight4k: '',
        audiometryLeft4k: '',
        audiometryResultRight: '',
        audiometryResultLeft: '',
        tympanometryRight: '',
        tympanometryLeft: '',
        oaeRight: '',
        oaeLeft: ''
      });
      
      setQuestionnaire({
        selectedAgeGroup: '4-6',
        answers: {}
      });
      
      setOverallResult(null);
    } else {
      const errorData = await response.json();
      alert(`Failed to save: ${errorData.error}`);
    }
  } catch (error) {
    console.error('Error submitting form:', error);
    alert('Error submitting school screening data');
  }
};


 const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting School Screening ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "school_screening",
          formName: "School_Screening",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `School_Screening_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 1200, margin: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Main Heading */}
      <Box sx={{ position: 'absolute', top: 260, right: 290 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
   <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
        School Screening
      </Typography>
        {/* Student Details Section */}
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Student Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={studentData.name}
              onChange={handleStudentChange}
              required
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              label="Age"
              name="age"
              type="number"
              value={studentData.age}
              onChange={handleStudentChange}
              required
              inputProps={{ min: 4, max: 18 }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Gender</InputLabel>
              <Select
                name="gender"
                value={studentData.gender}
                label="Gender"
                onChange={handleStudentChange}
                displayEmpty
                renderValue={value => value || "Select Gender"}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Date"
              name="date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={studentData.date}
              onChange={handleStudentChange}
              required
            />
          </Grid>
               <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="School Name"
              name="schoolName"
              value={studentData.schoolName}
              onChange={handleStudentChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Class/Grade"
              name="classGrade"
              value={studentData.classGrade}
              onChange={handleStudentChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>School Type</InputLabel>
              <Select
                name="schoolType"
                value={studentData.schoolType}
                label="School Type"
                onChange={handleStudentChange}
                displayEmpty
                renderValue={value => value || "Select School Type"}
              >
                <MenuItem value="Government">Government</MenuItem>
                <MenuItem value="Government Aided">Government Aided</MenuItem>
                <MenuItem value="Private">Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Medium</InputLabel>
              <Select
                name="medium"
                value={studentData.medium}
                label="Medium"
                onChange={handleStudentChange}
                displayEmpty
                renderValue={value => value || "Select Medium"}
              >
                <MenuItem value="Kannada">Kannada</MenuItem>
                <MenuItem value="English">English</MenuItem>
                <MenuItem value="Hindi">Hindi</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}> Board</InputLabel>
              <Select
                name="board"
                value={studentData.board}
                label="Board"
                onChange={handleStudentChange}
                displayEmpty
                renderValue={value => value || "Select Board"}
              >
                <MenuItem value="State">State</MenuItem>
                <MenuItem value="CBSE">CBSE</MenuItem>
                <MenuItem value="ICSE">ICSE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Socio-economic Status</InputLabel>
              <Select
                name="socioEconomicStatus"
                value={studentData.socioEconomicStatus}
                label="Socio-economic Status"
                onChange={handleStudentChange}
                displayEmpty
                renderValue={value => value || "Select Socio-economic Status"}
              >
                <MenuItem value="Upper Class">Upper Class</MenuItem>
                <MenuItem value="Upper Middle">Upper Middle</MenuItem>
                <MenuItem value="Lower Middle">Lower Middle</MenuItem>
                <MenuItem value="Upper Lower">Upper Lower</MenuItem>
                <MenuItem value="Lower">Lower</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth required>
              <InputLabel shrink={true}>Parents' Education</InputLabel>
              <Select
                name="parentsEducation"
                value={studentData.parentsEducation}
                label="Parents' Education"
                onChange={handleStudentChange}
                displayEmpty
                renderValue={value => value || "Select Parents' Education"}
              >
                <MenuItem value="Primary">Primary</MenuItem>
                <MenuItem value="Higher Primary">Higher Primary</MenuItem>
                <MenuItem value="Secondary">Secondary</MenuItem>
                <MenuItem value="Pre University">Pre University</MenuItem>
                <MenuItem value="Graduate">Graduate</MenuItem>
                <MenuItem value="Post Graduate">Post Graduate</MenuItem>
                <MenuItem value="PhD">PhD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Otoscopic Examination Section */}
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Otoscopic Examination
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Examination</TableCell>
                    <TableCell align="center">Right Ear</TableCell>
                    <TableCell align="center">Left Ear</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Otoscopic */}
                  <TableRow>
                    <TableCell>Otoscopic</TableCell>
                    <TableCell align="center">
                      <FormControl fullWidth>
                        <Select
                          name="rightOtoscopic"
                          value={earExamination.rightOtoscopic}
                          onChange={handleEarChange}
                          size="small"
                        >
                          <MenuItem value="Wax">Wax</MenuItem>
                          <MenuItem value="Debris">Debris</MenuItem>
                          <MenuItem value="Clear">Clear</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      <FormControl fullWidth>
                        <Select
                          name="leftOtoscopic"
                          value={earExamination.leftOtoscopic}
                          onChange={handleEarChange}
                          size="small"
                        >
                          <MenuItem value="Wax">Wax</MenuItem>
                          <MenuItem value="Debris">Debris</MenuItem>
                          <MenuItem value="Clear">Clear</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  
                  {/* Discharge */}
                  <TableRow>
                    <TableCell>Discharge</TableCell>
                    <TableCell align="center">
                      <RadioGroup 
                        row 
                        name="rightDischarge" 
                        value={earExamination.rightDischarge} 
                        onChange={handleEarChange}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                    <TableCell align="center">
                      <RadioGroup 
                        row 
                        name="leftDischarge" 
                        value={earExamination.leftDischarge} 
                        onChange={handleEarChange}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                  </TableRow>
                  
                  {/* Pain */}
                  <TableRow>
                    <TableCell>Pain</TableCell>
                    <TableCell align="center">
                      <RadioGroup 
                        row 
                        name="rightPain" 
                        value={earExamination.rightPain} 
                        onChange={handleEarChange}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                    <TableCell align="center">
                      <RadioGroup 
                        row 
                        name="leftPain" 
                        value={earExamination.leftPain} 
                        onChange={handleEarChange}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                  </TableRow>
                  
                  {/* Deformity */}
                  <TableRow>
                    <TableCell>Deformity</TableCell>
                    <TableCell align="center">
                      <RadioGroup 
                        row 
                        name="rightDeformity" 
                        value={earExamination.rightDeformity} 
                        onChange={handleEarChange}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                    <TableCell align="center">
                      <RadioGroup 
                        row 
                        name="leftDeformity" 
                        value={earExamination.leftDeformity} 
                        onChange={handleEarChange}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          
          {/* Specify Deformity */}
          {(earExamination.rightDeformity === 'Yes' || earExamination.leftDeformity === 'Yes') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specify Deformity"
                name="deformitySpecify"
                value={earExamination.deformitySpecify}
                onChange={handleEarChange}
                required
              />
            </Grid>
          )}
        </Grid>

        {/* Screening Tests Section */}
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Screening Tests
        </Typography>
        
        {/* Audiometry */}
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">Audiometry (dB HL)</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Frequency</TableCell>
                    <TableCell align="center">Right Ear</TableCell>
                    <TableCell align="center">Left Ear</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['500', '1k', '2k', '4k'].map((freq) => (
                    <TableRow key={freq}>
                      <TableCell>{freq.replace('k', ' kHz')}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          name={`audiometryRight${freq}`}
                          value={screeningTests[`audiometryRight${freq}`]}
                          onChange={handleScreeningChange}
                   
                          size="small"
                          sx={{ width: 80 }}
                        />
                        
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          name={`audiometryLeft${freq}`}
                          value={screeningTests[`audiometryLeft${freq}`]}
                          onChange={handleScreeningChange}
                       
                          size="small"
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>Result</TableCell>
                    <TableCell align="center">
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          name="audiometryResultRight"
                          value={screeningTests.audiometryResultRight}
                          onChange={handleScreeningChange}
                        >
                          <MenuItem value="Pass">Pass</MenuItem>
                          <MenuItem value="Refer">Refer</MenuItem>
                      
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          name="audiometryResultLeft"
                          value={screeningTests.audiometryResultLeft}
                          onChange={handleScreeningChange}
                        >
                          <MenuItem value="Pass">Pass</MenuItem>
                          <MenuItem value="Refer">Refer</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
        
        {/* Tympanometry */}
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">Tympanometry</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Right Ear</InputLabel>
                  <Select
                    name="tympanometryRight"
                    value={screeningTests.tympanometryRight}
                    onChange={handleScreeningChange}
                    label="Right Ear"
                    displayEmpty
                    renderValue={value => value || "Select Right Ear"}
                  >
                    <MenuItem value="A">A</MenuItem>
                    <MenuItem value="As">As</MenuItem>
                    <MenuItem value="B">B</MenuItem>
                    <MenuItem value="C">C</MenuItem>
                    <MenuItem value="Cs">Cs</MenuItem>
                    <MenuItem value="D">D</MenuItem>
                    <MenuItem value="E">E</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Left Ear</InputLabel>
                  <Select
                    name="tympanometryLeft"
                    value={screeningTests.tympanometryLeft}
                    onChange={handleScreeningChange}
                    label="Left Ear"
                    displayEmpty
                     renderValue={value => value || "Select Left Ear"}
                  >
                    <MenuItem value="A">A</MenuItem>
                    <MenuItem value="As">As</MenuItem>
                    <MenuItem value="B">B</MenuItem>
                    <MenuItem value="C">C</MenuItem>
                    <MenuItem value="Cs">Cs</MenuItem>
                    <MenuItem value="D">D</MenuItem>
                    <MenuItem value="E">E</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        
        {/* OAE */}
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">Otoacoustic Emissions (OAE)</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Right Ear</InputLabel>
                  <Select
                    name="oaeRight"
                    value={screeningTests.oaeRight}
                    onChange={handleScreeningChange}
                    label="Right Ear"
                    displayEmpty
                     renderValue={value => value || "Select Right Ear"}
                  >
                    <MenuItem value="Pass">Pass</MenuItem>
                    <MenuItem value="Refer">Refer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Left Ear</InputLabel>
                  <Select
                    name="oaeLeft"
                    value={screeningTests.oaeLeft}
                    onChange={handleScreeningChange}
                    label="Left Ear"
                    displayEmpty
                     renderValue={value => value || "Select Left Ear"}
                  >
                    <MenuItem value="Pass">Pass</MenuItem>
                    <MenuItem value="Refer">Refer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Questionnaire Section */}
        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
          Questionnaire
        </Typography>
        
        {/* Age Group Selection */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel shrink={true}> Select Age Group</InputLabel>
              <Select
                value={questionnaire.selectedAgeGroup}
                onChange={handleAgeGroupChange}
                label="Select Age Group"
                displayEmpty
                renderValue={value => value || "Select Age Group"}
              >
                <MenuItem value="4-6">4 to 6 years</MenuItem>
                <MenuItem value="6-8">6 to 8 years</MenuItem>
                <MenuItem value=">8">Above 8 years</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Questions */}
        <FormGroup>
          {questionsByAgeGroup[questionnaire.selectedAgeGroup].map((question, index) => (
            <Box 
              key={index} 
              sx={{ 
                mb: 2, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                backgroundColor: questionnaire.answers[index] === 'Yes' ? '#fff8e1' : 'inherit' 
              }}
            >
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>{question}</FormLabel>
              <RadioGroup 
                row 
                name={`question-${index}`} 
                value={questionnaire.answers[index] || ''} 
                onChange={handleQuestionChange(index)}
                sx={{ gap: 2 }}
              >
                <FormControlLabel value="Yes" control={<Radio color="warning" />} label="Yes" />
                <FormControlLabel value="No" control={<Radio color="success" />} label="No" />
              </RadioGroup>
            </Box>
          ))}
        </FormGroup>

        {/* Result Banner */}
        {overallResult && (
          <Box sx={{ 
            mt: 3, 
            p: 3, 
            borderRadius: 1,
            textAlign: 'center',
            backgroundColor: overallResult === 'refer' ? '#ffebee' : '#e8f5e9'
          }}>
            <Typography variant="h4" fontWeight="bold" color={overallResult === 'refer' ? 'error' : 'success'}>
              {overallResult === 'refer' ? 'REFER' : 'PASS'}
            </Typography>
            <Typography variant="subtitle1" mt={1}>
             
             
            </Typography>
          </Box>
        )}
       <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
        {/* Submit Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button 
            type="submit"
            variant="contained" 
            color="primary" 
            size="large" 
            sx={{ fontSize: '1.1rem', px: 4, py: 1.5 }}
          >
            Submit
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SchoolScreeningForm;